import { Button, Col, Row } from "antd";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { IGenerateOtpRequest } from "../../../models/otp/request";
import { IGenerateOtpResponse } from "../../../models/otp/response";
import EndPoints from "../../../services/end-points";
import { execute } from "../../../utils/helpers/execute";
import { cleanMobileNumber, isValidSyrianMobile } from "../../../utils/helpers/phone-cleaner";
import FieldBuilder from "../../form-components/field-builder";
import styles from "./style.module.scss";

interface OtpGeneratorFormProps {
  onSuccess: (result: IGenerateOtpResponse) => void;
}

const OtpGeneratorForm: React.FC<OtpGeneratorFormProps> = ({ onSuccess }) => {
  const { t } = useTranslation();
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  const methods = useForm<IGenerateOtpRequest>({
    defaultValues: {
      mobile: "",
      validityMinutes: 5,
      sendMessage: false,
    },
  });

  const { handleSubmit, setValue, watch, setError } = methods;

  // Clean mobile number on change
  const handleMobileChange = (value: string) => {
    const cleaned = cleanMobileNumber(value);
    setValue("mobile", cleaned);
  };

  const onSubmit = async (data: IGenerateOtpRequest) => {
    // Clean and validate mobile number before submitting
    const cleanedMobile = cleanMobileNumber(data.mobile);

    if (!isValidSyrianMobile(cleanedMobile)) {
      setError("mobile", {
        type: "manual",
        message: t("invalid_mobile_format"),
      });
      return;
    }

    await execute({
      callback: async () => {
        setLoadingSubmit(true);
        const result = await EndPoints.otp.generateOtp({
          ...data,
          mobile: cleanedMobile,
        });
        onSuccess(result.data);
      },
      fallback: (error) => {
        // Error handling is automatic via execute helper
      },
      finallyCallback: () => {
        setLoadingSubmit(false);
      },
      throwException: false,
    });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className={styles.otpForm}>
        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <FieldBuilder
              name="mobile"
              label={t("mobile_number")}
              input={{
                type: "text"
              }}
              rules={{
                required: true,
              }}
              onChange={(e) => handleMobileChange(e.target.value)}
            />
          </Col>

          <Col xs={24}>
            <FieldBuilder
              name="validityMinutes"
              label={t("validity_minutes")}
              input={{ type: "number" }}
              rules={{
                required: true,
                min: 1,
                max: 1440,
              }}
            />
          </Col>

          <Col xs={24}>
            <FieldBuilder
              name="sendMessage"
              label={t("send_message")}
              input={{ type: "switch" }}
              rules={{ required: false }}
            />
          </Col>

          <Col xs={24}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loadingSubmit}
              block
              size="large"
            >
              {t("generate_otp")}
            </Button>
          </Col>
        </Row>
      </form>
    </FormProvider>
  );
};

export default OtpGeneratorForm;
